import { useState } from "react";
import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { yupResolver } from "@hookform/resolvers/yup";

// importing custom components
import { CustomInput } from "../components/UserInput";
import { forgotPasswordSchema } from "../yup";
import { Button, Message } from "../components";

// import styles and colors
import { ReactComponent as Tavoraq } from "../assets/images/TavoraqLogo.svg";
import style from "../components/UserInput/input.module.css";
import { colors } from "../theme/colors";
import { appConfig } from "../hooks/useConfig";

type FormState = typeof forgotPasswordSchema.__outputType;

const ForgotPassword = () => {
  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(loginSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormState>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle form submission
  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { email } = values;
      await fetch(
        `https://${appConfig.AUTH0_DOMAIN}/dbconnections/change_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_id: appConfig.AUTH0_CLIENT_ID,
            email,
            connection: "Username-Password-Authentication", // Your Auth0 connection name
          }),
        }
      );
      setIsLoading(false);
      setMessage({
        type: "success",
        text: "We've just sent you an email to reset your password.",
      });
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.data?.detail?.toString() || "Please enter valid details",
      });
      setIsLoading(false);
    }
  };

  // Render the forgot password form
  return (
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        height: "calc(100vh - 97px)",
      }}
    >
      <Box className={style.boxShadow}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            margin: "auto",
            width: "395px",
            height: "100%",
          }}
          noValidate
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box textAlign="center" paddingBlockEnd={"20px"}>
            <Tavoraq />
          </Box>

          <Typography
            paddingBlockEnd={2}
            textAlign="center"
            noWrap
            component="p"
            fontSize={16}
            fontWeight={700}
          >
            Forgot Password
          </Typography>
          <Box className={style.inputs}>
            <CustomInput
              name="email"
              label="Email"
              type="text"
              value={watch("email")}
              isLoading={isLoading}
              isError={errors.email}
              register={register}
              showIcon={false}
            />
            {message && <Message message={message} />}
          </Box>

          <Button
            label="Request reset link"
            loading={isLoading}
            type="submit"
            variant="contained"
            sx={{
              width: "100%",
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "500",
              height: "60px",
              marginTop: "60px",
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;